import { SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  calculateBulkDeadlineChanges,
  parseBulkDeadlineAssigneeTarget,
  type BulkDeadlineAssigneeTarget,
  type BulkDeadlineChanges,
  type BulkDeadlineStatusTarget,
} from '@/lib/deadlines/bulk-edit';
import { Button } from '@/components/ui/button';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import type { MatterMember } from '@/types/base';
import type { Task } from '@/components/ui/columns-tasks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getTaskStateConfig } from '@/components/tasks/constants';
import { formatRegionalDate } from '@/utils/date-utils';

type BulkPreviewRow = {
  id: string;
  dateLabel: string;
  name: string;
  currentStatusLabel: string;
  nextStatusLabel?: string;
  currentAssigneeLabel: string;
  nextAssigneeLabel?: string;
  statusWillChange: boolean;
  assigneeWillChange: boolean;
  statusSkipped: boolean;
  hasAnyChange: boolean;
};

type BulkEditDeadlinesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDeadlines: Task[];
  members: MatterMember[];
  isApplying: boolean;
  onApply: (changes: BulkDeadlineChanges) => Promise<void>;
};

function getAssigneeLabel(deadline: Task) {
  return deadline.assigned_to_name || 'Unassigned';
}

function getSelectedAssigneeLabel(target: BulkDeadlineAssigneeTarget, members: MatterMember[]) {
  if (target === 'unchanged') return undefined;
  if (target === 'unassigned') return 'Unassigned';
  return members.find((member) => member.user_guid === target)?.name || 'Selected assignee';
}

export function BulkEditDeadlinesDialog({
  open,
  onOpenChange,
  selectedDeadlines,
  members,
  isApplying,
  onApply,
}: BulkEditDeadlinesDialogProps) {
  const locale = useMatterLocale();
  const [statusTarget, setStatusTarget] = useState<BulkDeadlineStatusTarget>('unchanged');
  const [assigneeTarget, setAssigneeTarget] = useState<BulkDeadlineAssigneeTarget>('unchanged');

  useEffect(() => {
    if (!open) {
      setStatusTarget('unchanged');
      setAssigneeTarget('unchanged');
    }
  }, [open]);

  const changes = useMemo(
    () => calculateBulkDeadlineChanges(selectedDeadlines, statusTarget, assigneeTarget),
    [assigneeTarget, selectedDeadlines, statusTarget],
  );

  const nextStatusLabel = changes.state ? getTaskStateConfig(changes.state).label : undefined;
  const nextAssigneeLabel = getSelectedAssigneeLabel(assigneeTarget, members);

  const previewRows = useMemo<BulkPreviewRow[]>(() => {
    const stateGuidSet = new Set(changes.stateTaskGuids);
    const assigneeGuidSet = new Set(changes.assigneeTaskGuids);
    const skippedStateGuidSet = new Set(changes.skippedManualStateGuids);

    return selectedDeadlines.map((deadline) => {
      const statusWillChange = stateGuidSet.has(deadline.id);
      const assigneeWillChange = assigneeGuidSet.has(deadline.id);
      const statusSkipped = skippedStateGuidSet.has(deadline.id);

      return {
        id: deadline.id,
        dateLabel: formatRegionalDate(deadline.date, locale),
        name: deadline.description || deadline.id,
        currentStatusLabel: getTaskStateConfig(deadline.state).label,
        nextStatusLabel: statusWillChange ? nextStatusLabel : undefined,
        currentAssigneeLabel: getAssigneeLabel(deadline),
        nextAssigneeLabel: assigneeWillChange ? nextAssigneeLabel : undefined,
        statusWillChange,
        assigneeWillChange,
        statusSkipped,
        hasAnyChange: statusWillChange || assigneeWillChange,
      };
    });
  }, [changes, locale, nextAssigneeLabel, nextStatusLabel, selectedDeadlines]);

  const changedPreviewRows = previewRows.filter((row) => row.hasAnyChange);
  const unchangedCount = selectedDeadlines.length - changedPreviewRows.length;
  const hasInput = statusTarget !== 'unchanged' || assigneeTarget !== 'unchanged';
  const hasChanges = changes.changedDeadlineIds.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStatusTarget('unchanged');
      setAssigneeTarget('unchanged');
    }
    onOpenChange(nextOpen);
  };

  const handleApply = async () => {
    if (!hasChanges) return;
    await onApply(changes);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex h-[86vh] max-h-[820px] flex-col overflow-hidden sm:max-w-6xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Edit Deadlines</DialogTitle>
          <DialogDescription>
            Review changes for {selectedDeadlines.length} selected deadline{selectedDeadlines.length !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid shrink-0 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-900">Status</label>
            <Select
              value={statusTarget}
              onValueChange={(value) => setStatusTarget(value as BulkDeadlineStatusTarget)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unchanged">Leave unchanged</SelectItem>
                <SelectItem value="Extracted">Needs Confirmation</SelectItem>
                <SelectItem value="Confirmed">Confirmed</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-900">Assignee</label>
            <Select
              value={String(assigneeTarget)}
              onValueChange={(value) => setAssigneeTarget(parseBulkDeadlineAssigneeTarget(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unchanged">Leave unchanged</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.user_guid} value={member.user_guid}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex h-10 shrink-0 items-center rounded-lg border border-stone-200 bg-stone-50 px-3 text-xs text-stone-500">
          <span className="font-medium text-stone-900">{changes.changedDeadlineIds.length}</span>
          <span className="ml-1">deadline{changes.changedDeadlineIds.length !== 1 ? 's' : ''} changing</span>
          <span className="mx-2 text-stone-300">/</span>
          <span>{changes.stateTaskGuids.length} status change{changes.stateTaskGuids.length !== 1 ? 's' : ''}</span>
          <span className="mx-2 text-stone-300">/</span>
          <span>{changes.assigneeTaskGuids.length} assignee change{changes.assigneeTaskGuids.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-stone-200">
          <div className="h-9 shrink-0 border-b border-stone-200 bg-white px-3 py-2 text-xs font-medium uppercase tracking-wide text-stone-500">
            Preview
          </div>

          {changedPreviewRows.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12 text-center">
              <div>
                <p className="text-sm font-medium text-stone-900">
                  {hasInput ? 'Selected edits do not change these deadlines' : 'No deadline changes yet'}
                </p>
                <p className="mt-1 text-sm text-stone-500">
                  {hasInput
                    ? 'Every selected deadline already matches the requested status and assignee.'
                    : 'Choose a status or assignee to preview exactly which deadlines will be updated.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              {changedPreviewRows.map((previewRow) => (
                <div
                  key={previewRow.id}
                  className="grid min-h-16 grid-cols-[112px_minmax(0,1fr)_300px] items-start gap-4 border-b border-stone-100 px-3 py-2.5 last:border-b-0"
                >
                  <div className="pt-0.5 text-xs font-medium leading-5 text-stone-500">
                    {previewRow.dateLabel}
                  </div>

                  <div
                    className="min-w-0 text-xs font-medium leading-5 text-stone-900"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                    title={previewRow.name}
                  >
                    {previewRow.name}
                  </div>

                  <div className="min-w-0 space-y-1">
                    {previewRow.statusWillChange && previewRow.nextStatusLabel && (
                      <div className="grid min-w-0 grid-cols-[58px_minmax(0,1fr)] gap-1.5 text-[11px] leading-4">
                        <span className="font-medium uppercase tracking-wide text-stone-400">Status</span>
                        <span className="min-w-0 truncate">
                          <span className="text-stone-500">{previewRow.currentStatusLabel}</span>
                          <span className="px-1.5 text-stone-400">-&gt;</span>
                          <span className="font-medium text-stone-900">{previewRow.nextStatusLabel}</span>
                        </span>
                      </div>
                    )}

                    {previewRow.assigneeWillChange && previewRow.nextAssigneeLabel && (
                      <div className="grid min-w-0 grid-cols-[58px_minmax(0,1fr)] gap-1.5 text-[11px] leading-4">
                        <span className="font-medium uppercase tracking-wide text-stone-400">Assignee</span>
                        <span className="min-w-0 truncate">
                          <span className="text-stone-500">{previewRow.currentAssigneeLabel}</span>
                          <span className="px-1.5 text-stone-400">-&gt;</span>
                          <span className="font-medium text-stone-900">{previewRow.nextAssigneeLabel}</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {(unchangedCount > 0 || changes.skippedManualStateGuids.length > 0) && hasInput && (
          <p className="shrink-0 text-xs text-stone-500">
            {unchangedCount > 0
              ? `${unchangedCount} selected deadline${unchangedCount !== 1 ? 's' : ''} already match the selected edits.`
              : null}
            {changes.skippedManualStateGuids.length > 0
              ? ` ${changes.skippedManualStateGuids.length} manual deadline${changes.skippedManualStateGuids.length !== 1 ? 's' : ''} cannot be moved to Needs Confirmation.`
              : null}
          </p>
        )}

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isApplying}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={isApplying || !hasChanges || selectedDeadlines.length === 0}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {isApplying
              ? 'Applying...'
              : `Apply to ${changes.changedDeadlineIds.length} deadline${changes.changedDeadlineIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
